import { api } from './api';
import { formatDateOnly } from './taskDates';

/**
 * Recurring tasks, as the web app sees them.
 *
 * The server does all the stepping and all the spawning (server/src/lib/
 * recurrence.ts). What lives here is how a rule reads, and the one thing only
 * the browser can do: say what day it is, so the server can spawn whatever
 * that day has brought.
 */

export type RepeatFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

/** Mirrors the server's `RepeatRule`. Null on a task means it does not repeat. */
export type RepeatRule = {
  freq: RepeatFreq;
  interval: number;
  /** 0 = Sunday .. 6 = Saturday, sorted. Empty means the due date's own
   *  weekday. Only meaningful for WEEKLY. */
  weekdays: number[];
  /** The last day an instance may fall on, as `YYYY-MM-DD`. Null repeats
   *  forever. */
  until: string | null;
};

export const REPEAT_FREQS: RepeatFreq[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];

const UNITS: Record<RepeatFreq, [singular: string, plural: string]> = {
  DAILY: ['day', 'days'],
  WEEKLY: ['week', 'weeks'],
  MONTHLY: ['month', 'months'],
  YEARLY: ['year', 'years'],
};

const ADVERBS: Record<RepeatFreq, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
};

/** For the picker's unit select: "day" beside a 1, "days" beside anything else. */
export const repeatUnit = (freq: RepeatFreq, interval: number): string =>
  UNITS[freq][interval === 1 ? 0 : 1];

/** Sunday-first, matching `Date.getDay()` and the server's numbering. */
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Monday-first, which is how the picker lays them out. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * The rule as one short phrase: "Weekly", "Every 2 weeks on Mon, Wed",
 * "Monthly until Dec 31".
 */
export function describeRepeat(rule: RepeatRule): string {
  let text =
    rule.interval === 1
      ? ADVERBS[rule.freq]
      : `Every ${rule.interval} ${repeatUnit(rule.freq, rule.interval)}`;

  if (rule.freq === 'WEEKLY' && rule.weekdays.length > 0) {
    const days = WEEKDAY_ORDER.filter((day) => rule.weekdays.includes(day));
    text += ` on ${days.map((day) => WEEKDAY_SHORT[day]).join(', ')}`;
  }

  if (rule.until) text += ` until ${formatDateOnly(rule.until)}`;
  return text;
}

/**
 * Something in this tab added or removed an instance of a series.
 *
 * A window event for the reason `remindersChanged` is one: the writes that can
 * cause it are the two choke points in lib/tasks.ts plus the catch-up below,
 * and none of them should have to know which lists are on screen. Each list
 * listens and quietly refetches -- it is the only one that knows whether the
 * new instance belongs on it.
 */
const SERIES_EVENT = 'hive:series-changed';

export function seriesChanged(): void {
  window.dispatchEvent(new Event(SERIES_EVENT));
}

export function onSeriesChanged(listener: () => void): () => void {
  window.addEventListener(SERIES_EVENT, listener);
  return () => window.removeEventListener(SERIES_EVENT, listener);
}

/**
 * Spawn the next instance of everything whose due date has arrived by `today`
 * -- the reader's own local day, from `todayInputValue()`.
 *
 * Announces only when something was actually spawned, so a return to the tab
 * on an ordinary day costs one small request and no refetches.
 */
export async function catchUpRecurrence(today: string): Promise<void> {
  const { spawnedIds } = await api.post<{ spawnedIds: string[] }>(
    '/api/tasks/recurrence/catch-up',
    { today },
  );
  if (spawnedIds.length > 0) seriesChanged();
}
